import React, {useMemo, useState} from 'react';
import {AdditionalRowComponentProps} from '@src/@types/components/ControlledList';
import {IModelFileToImportModel} from '@src/@interfaces/models/ModelFileToImport/IModelFileToImportModel';
import {useQuery} from '@tanstack/react-query';
import app from '@src/helpers/app';
import RenderIf from '@src/components/RenderIf/RenderIf';
import NowLoadingMessage from '@src/components/NowLoadingMessage/NowLoadingMessage';
import {IImportConfig} from '@src/@interfaces/components/Import/IImportConfig';
import ImportSettings from '@src/components/_views/configurable/import-view/file-list/additional-row/ImportSettings';
import {FormProvider, SubmitHandler, useForm} from 'react-hook-form';
import {TForm} from '@src/components/_views/configurable/import-view/file-list/.types';
import Table from '@src/components/_views/configurable/import-view/file-list/additional-row/table/Table';
import Translate from '@src/components/Translate/Translate';
import handleError from '@src/helpers/handleError';
import ErrorRepresentation from '@src/components/ErrorRepresentation/ErrorRepresentation';

export default function AdditionalRow(props: AdditionalRowComponentProps<IModelFileToImportModel, {
    controllerName: string,
    config: IImportConfig,
}>) {
    const {model, controllerName, context, config} = props;
    const {controllerName: wrappedControllerName} = context ?? {};

    const formMethods = useForm<TForm>({
        defaultValues: {
            skipFirstRows: model?.settings?.skipFirstRows ?? 0,
            hasHeader: model?.settings?.hasHeader ?? false,
            headerRowIndex: null,
            additionalColumnsNumber: model?.settings?.additionalColumnsNumber ?? 0,
            columns: model?.settings?.columns ?? [],
        },
    });

    const queryKey = app('queryKeys').getDetailsOfRecordQueryKey(
            wrappedControllerName ?? 'unknown',
            model?.id,
    );
    const {data, isFetching} = useQuery<Array<string[]>, Error>(
            queryKey,
            () => {
                return app('http').route('abstract-models.import.show', {
                    id: String(model?.id),
                }, {
                    json: {
                        'controller-name': controllerName,
                    }
                });
            },
            {
                useErrorBoundary: false,
                enabled: controllerName != null,
                staleTime: 5 * 60 * 1000,
            }
    );

    const additionalColumnsNumber = formMethods.watch('additionalColumnsNumber') ?? 0;

    const {columns} = useMemo(() => {
        const columnNumber = (data ?? [])?.reduce((max, cells) => {
            return Math.max(max, cells.length);
        }, 0);

        return {columns: Array.from({length: columnNumber + +additionalColumnsNumber})};
    }, [data, additionalColumnsNumber]);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const onSubmit: SubmitHandler<TForm> = data => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);
        setError(null);

        return app('http').route('abstract-models.import.update-settings', {
            id: String(model?.id),
        }, {
            json: {
                'controller-name': controllerName,
                ...data,
            }
        }).catch(e => {
            handleError(e);
            setError(e);
        }).finally(() => {
            setIsLoading(false);
        });
    };

    return <FormProvider {...formMethods}>
        {error ? <ErrorRepresentation
                error={error}
                className="mb-3"
                withoutStackTrace
                withoutErrorName
                outsideContainer
        /> : null}

        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
            <RenderIf expression={isFetching}>
                <NowLoadingMessage/>
            </RenderIf>

            <RenderIf expression={data}>
                <ImportSettings/>

                <Table
                        model={model}
                        columns={columns}
                        data={data}
                        config={config}
                />

                <div className="mt-3 text-end">
                    <Translate
                            tag="button"
                            message="Forms.save_changes"
                            type="submit"
                            className="btn btn-primary"
                    />
                </div>
            </RenderIf>
        </form>
    </FormProvider>;
}
